"use client";

import React from "react";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { tinaFieldSafe, cn } from "@/utils";
import { PLATFORMS_ICONS } from "@/components/Footer";
import Link from "next/link";
import { getMemberSlug } from "@/lib/team-members";
import { ArrowRightIcon, FileText } from "lucide-react";

function TeamMemberPostCount({
  count,
  href,
}: {
  count?: number;
  href?: string;
}) {
  if (!count || count < 1) return null;

  const content = (
    <>
      <FileText className="size-2.5 shrink-0" aria-hidden />
      {count}
    </>
  );

  const className = cn(
    "inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground transition-colors",
    href &&
    "rounded-full border border-gray-600 px-2 py-1 hover:border-gray-400 hover:text-white"
  );

  if (href) {
    return (
      <Link
        href={href}
        className={className}
        aria-label={`View ${count} ${count === 1 ? "post" : "posts"}`}
        onClick={(event) => event.stopPropagation()}
      >
        {content}
      </Link>
    );
  }

  return <span className={className}>{content}</span>;
}

function TeamMemberSocials({
  socials,
  member,
  variant = "default",
}: {
  socials?: { platform?: string | null; url?: string | null }[] | null;
  member: any;
  variant?: "default" | "solid";
}) {
  if (!socials?.length) return null;

  const isSolid = variant === "solid";

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {socials.map((social, index) => {
        const Icon =
          PLATFORMS_ICONS[social?.platform?.toLowerCase() || ""] || null;

        if (!Icon) return null;

        return (
          <a
            key={index}
            href={social?.url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "group cursor-pointer rounded-full p-2 transition-all duration-300",
              isSolid
                ? "border border-white/30 bg-white shadow-sm hover:bg-white/90"
                : "border border-gray-700 hover:bg-white"
            )}
            title={social?.platform || undefined}
            aria-label={`${member?.name || "Team member"} on ${social?.platform ?? "social"}`}
            data-tina-field={tinaFieldSafe(member, `socials.[${index}]`)}
          >
            <Icon
              className={cn(
                "size-4 transition-all duration-300",
                isSolid
                  ? "text-gray-900"
                  : "text-muted-foreground group-hover:text-black"
              )}
              aria-hidden
            />
          </a>
        );
      })}
    </div>
  );
}

function TeamMemberDialogActions({
  profileSlug,
  postCount,
  showProfileLink,
}: {
  profileSlug?: string;
  postCount?: number;
  showProfileLink?: boolean;
}) {
  const hasProfileLink = Boolean(showProfileLink && profileSlug);
  const hasPosts = Boolean(postCount && postCount >= 1 && profileSlug);

  if (!hasProfileLink && !hasPosts) return null;

  return (
    <div className="mt-4 flex flex-wrap justify-end gap-3 md:mt-0">
      {hasPosts ? (
        <Button variant="link-underline" size="sm" asChild>
          <Link href={`/team/${profileSlug}#posts`}>
            View Posts ({postCount})
          </Link>
        </Button>
      ) : null}
      {hasProfileLink ? (
        <Button variant="outline" size="sm" asChild>
          <Link href={`/team/${profileSlug}`}>
            View Profile
            <ArrowRightIcon className="size-4" />
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

export function TeamMemberProfile({
  member,
  showProfileLink = false,
  variant = "dialog",
  postCount = 0,
}: {
  member: any;
  showProfileLink?: boolean;
  variant?: "header" | "dialog";
  postCount?: number;
}) {
  if (!member) return null;

  const profileSlug = getMemberSlug(member);

  const isHeader = variant === "header";

  if (isHeader) {
    return (
      <article className="w-full after:block after:clear-both after:content-['']">
        <div className="relative mx-auto mb-6 aspect-square w-full max-w-[320px] overflow-hidden bg-gray-900 md:float-left md:mb-4 md:mr-8 md:max-w-[360px] lg:mr-12 lg:max-w-[420px] xl:max-w-[420px] [shape-outside:margin-box]">
          <Image
            src={member?.image || "/placeholder.png"}
            alt={member?.name || "Team member"}
            fill
            className="object-cover"
            priority
            data-tina-field={tinaFieldSafe(member, "image")}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1
              className="mb-2 text-balance text-3xl font-title md:mb-3 md:text-4xl lg:text-5xl"
              data-tina-field={tinaFieldSafe(member, "name")}
            >
              {member?.name}
            </h1>

            {member?.role ? (
              <p
                className="h5 !text-muted-foreground-light"
                data-tina-field={tinaFieldSafe(member, "role")}
              >
                {member.role}
              </p>
            ) : null}
          </div>
          <TeamMemberSocials
            socials={member?.socials}
            member={member}
            variant="solid"
          />
        </div>

        {member?.description ? (
          <p
            className="mt-4 whitespace-pre-wrap text-lg leading-relaxed text-white md:mt-6 md:text-xl"
            data-tina-field={tinaFieldSafe(member, "description")}
          >
            {member.description}
          </p>
        ) : null}
      </article>
    );
  }

  return (
    <article className="w-full md:grid md:grid-cols-[320px_minmax(0,1fr)] md:items-start md:gap-8 lg:grid-cols-[480px_minmax(0,1fr)]">
      <div className="relative mx-auto mb-6 aspect-square w-full max-h-[320px] max-w-[320px] shrink-0 overflow-hidden bg-gray-900 md:mx-0 md:mb-0 lg:max-h-[480px] lg:max-w-[480px]">
        <Image
          src={member?.image || "/placeholder.png"}
          alt={member?.name || "Team member"}
          fill
          className="object-cover"
          priority={false}
          data-tina-field={tinaFieldSafe(member, "image")}
        />
      </div>

      <div className="flex min-w-0 flex-col md:min-h-[320px] lg:min-h-[480px]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1
              className="h4 font-normal"
              data-tina-field={tinaFieldSafe(member, "name")}
            >
              {member?.name}
            </h1>
            <p className="h6" data-tina-field={tinaFieldSafe(member, "role")}>
              {member?.role}
            </p>
          </div>
          <TeamMemberSocials socials={member?.socials} member={member} />
        </div>

        {member?.description ? (
          <p
            className="mt-4 flex-1 whitespace-pre-wrap p-sm text-white md:mt-6"
            data-tina-field={tinaFieldSafe(member, "description")}
          >
            {member.description}
          </p>
        ) : (
          <div className="flex-1" />
        )}

        <TeamMemberDialogActions
          profileSlug={profileSlug}
          postCount={postCount}
          showProfileLink={showProfileLink}
        />
      </div>
    </article>
  );
}

// Team Member Card Component
export function TeamMemberCard({
  member,
  onClick,
  postCount = 0,
}: {
  member: any;
  onClick: () => void;
  postCount?: number;
}) {
  if (!member) return null;

  return (
    <Card
      wrapperClassName="py-4"
      className="h-full cursor-pointer"
      animateOnHover={true}
      backgroundVariant="light"
      onClick={onClick}
    >
      <CardContent className="flex flex-col gap-4 px-4">
        <div className="relative w-full aspect-square bg-gray-900">
          <Image
            src={member?.image || "/placeholder.png"}
            alt={member?.name || "Team member"}
            fill
            className="object-cover"
            data-tina-field={tinaFieldSafe(member, "image")}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <h3
              className="h6 min-w-0"
              data-tina-field={tinaFieldSafe(member, "name")}
            >
              {member?.name}
            </h3>
            {postCount >= 1 ? (
              <TeamMemberPostCount
                count={postCount}
                href={`/team/${getMemberSlug(member)}`}
              />
            ) : null}
          </div>
          <p
            className="!text-sm text-muted-foreground"
            data-tina-field={tinaFieldSafe(member, "role")}
          >
            {member?.role}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Team Member Dialog Component
export function TeamMemberDialog({
  member,
  open,
  onClose,
  postCount = 0,
}: {
  member: any;
  open: boolean;
  onClose: () => void;
  postCount?: number;
}) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="lg:max-w-6xl">
        <DialogBody>
          <TeamMemberProfile
            member={member}
            showProfileLink
            postCount={postCount}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
